import { sendRawTcp } from "@/lib/printTransport";
import { getActiveStoreRecord } from "@/lib/storeContext";
import { toast } from "sonner";

const STX = "\x02";
const ETX = "\x03";

function xorLrc(s) {
  let x = 0;
  for (let i = 0; i < s.length; i += 1) x ^= s.charCodeAt(i) & 0xff;
  return String.fromCharCode(x);
}

/** Protocollo ECR-POS 17: vendita, importo in centesimi (12 cifre), LRC XOR fino a ETX. */
export function buildProtocol17Payment(amountEur) {
  const cents = Math.round(Number(amountEur) * 100);
  const amt = String(Math.max(0, Number.isFinite(cents) ? cents : 0)).padStart(12, "0").slice(-12);
  const inner = `1701${amt}${ETX}`;
  return `${STX}${inner}${xorLrc(inner)}`;
}

export function isCardPosConfigured(store) {
  if (!store || !store.card_pos_enabled) return false;
  const host = String(store.card_pos_host || "").trim();
  const port = Number(store.card_pos_port);
  return !!host && port > 0 && port <= 65535;
}

export function cardPortion(method, breakdown, amount) {
  const total = Math.round(Number(amount || 0) * 100) / 100;
  if (method === "card") return total;
  if (method === "mixed" && breakdown && typeof breakdown === "object") {
    return Math.round(Number(breakdown.card || 0) * 100) / 100;
  }
  return 0;
}

function parsePosOutcome(raw) {
  const inner = String(raw || "").replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, "");
  if (!inner.trim()) return { ok: false, reason: "empty" };
  const upper = inner.toUpperCase();
  if (/NACK|DECLIN|RIFIUT|ANNULL|\bKO\b|FAIL|ERROR/.test(upper)) return { ok: false, reason: "declined" };
  if (inner.startsWith("17") && inner.length >= 4) {
    const code = inner.slice(2, 4);
    if (code === "00") return { ok: true };
    return { ok: false, reason: "declined" };
  }
  if (upper.includes("APPROV") || upper.includes("AUTORIZZ") || /\bOK\b/.test(upper)) return { ok: true };
  return { ok: false, reason: "unknown" };
}

export async function requestProtocol17(store, amountEur) {
  const host = String(store.card_pos_host || "").trim();
  const port = Number(store.card_pos_port) || 1000;
  const payload = buildProtocol17Payment(amountEur);
  const sent = await sendRawTcp({
    host,
    port,
    data: payload,
    encoding: "ISO-8859-1",
    timeoutMs: 90000,
    expectResponse: true,
  });
  if (!sent.ok) {
    const hint = sent.reason === "qz_offline"
      ? "Avvia QZ Tray sul PC (stessa LAN del POS) oppure usa il tablet Velta."
      : (sent.detail || "Collegamento al POS fallito.");
    return { ok: false, mode: "pos", message: `POS ${host}:${port}: ${hint} Scontrino non emesso.` };
  }
  const outcome = parsePosOutcome(sent.response);
  if (outcome.ok) return { ok: true, mode: "pos", response: sent.response || "" };
  if (outcome.reason === "empty") {
    return { ok: false, mode: "pos", message: `POS ${host}:${port}: nessuna risposta. Scontrino non emesso.` };
  }
  if (outcome.reason === "declined") {
    return { ok: false, mode: "pos", message: "Pagamento carta rifiutato o annullato sul POS. Scontrino non emesso." };
  }
  return { ok: false, mode: "pos", message: `POS ${host}:${port}: risposta non riconosciuta. Scontrino non emesso.` };
}

/**
 * Se la sede ha IP:porta POS, autorizza la carta in LAN prima dello scontrino.
 * Se non è configurato, Carta resta manuale sul terminale del negozio.
 */
export async function authorizeCardIfNeeded({ method, breakdown, amount }) {
  const cardAmt = cardPortion(method, breakdown, amount);
  if (cardAmt < 0.009) return { ok: true, mode: "skip" };
  const store = getActiveStoreRecord();
  if (!isCardPosConfigured(store)) return { ok: true, mode: "manual" };
  toast.info(`Invio ${cardAmt.toFixed(2).replace(".", ",")} € al POS… attendere esito sulla carta`);
  return requestProtocol17(store, cardAmt);
}
